<template>
  <div class="po-report">
    <KTCodePreview v-bind:title="'Báo cáo công nợ Nhà phân phối'">
      <template v-slot:toolbar>
        <div class="row">
          <div class="ml-3">
            <b-button size="sm" variant="secondary" @click="goBack">
              <i class="flaticon2-back"></i> Quay lại
            </b-button>
          </div>
        </div>
      </template>

      <template v-slot:preview>
        <b-tabs v-model="activeTab" content-class="mt-3">
          <!-- Tab 1: Công nợ NPP -->
          <b-tab title="Công nợ NPP" active>
            <div class="po-filter-card mb-3">
              <b-row>
                <b-col cols="12" md="4">
                  <label class="po-label">Nhà phân phối</label>
                  <b-form-select
                    size="sm"
                    v-model="debtFilter.providerId"
                    :options="providerOptions"
                    class="select-style"
                  />
                </b-col>
                <b-col cols="6" md="2" class="d-flex align-items-end">
                  <b-button block variant="primary" size="sm" @click="loadDebt">
                    <i class="flaticon2-magnifier-tool"></i> Lọc
                  </b-button>
                </b-col>
              </b-row>
            </div>

            <b-table
              :items="debtRows"
              :fields="debtFields"
              :busy="debtLoading"
              responsive
              bordered
              hover
              show-empty
              empty-text="Không có dữ liệu công nợ."
              :tbody-tr-class="debtRowClass"
              class="po-debt-table"
            >
              <template #table-busy>
                <div class="text-center text-primary my-3">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Đang tải...</strong>
                </div>
              </template>
              <template #cell(committedDebt)="r">
                {{ formatPrice(r.item.committedDebt) }}
              </template>
              <template #cell(payableDebt)="r">
                {{ formatPrice(r.item.payableDebt) }}
              </template>
              <template #cell(totalDebt)="r">
                <b>{{ formatPrice(r.item.totalDebt) }}</b>
              </template>
              <template #cell(creditLimit)="r">
                {{ formatPrice(r.item.creditLimit) }}
              </template>
              <template #cell(overLimit)="r">
                <b-badge :variant="r.item.overLimit ? 'danger' : 'success'">
                  <i
                    :class="[
                      'po-over-icon',
                      r.item.overLimit
                        ? 'fas fa-exclamation-triangle'
                        : 'fas fa-check',
                    ]"
                  ></i>
                  {{ r.item.overLimit ? 'Vượt hạn mức' : 'Trong hạn mức' }}
                </b-badge>
              </template>
              <template #cell(actions)="r">
                <b-button
                  v-if="canPay && Number(r.item.payableDebt) > 0"
                  size="sm"
                  variant="outline-success"
                  @click="openPayModal(r.item)"
                >
                  <i class="fas fa-money-bill-wave po-pay-icon"></i> Thanh toán
                </b-button>
              </template>
            </b-table>
          </b-tab>

          <!-- Tab 2: Hàng đang về theo PO -->
          <b-tab title="Hàng đang về" @click="loadPipeline">
            <b-table
              :items="pipelineRows"
              :fields="pipelineFields"
              :busy="pipelineLoading"
              responsive
              bordered
              hover
              show-empty
              empty-text="Không có xe nào đang về theo PO."
              class="po-pipeline-table"
            >
              <template #table-busy>
                <div class="text-center text-primary my-3">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Đang tải...</strong>
                </div>
              </template>
              <template #cell(poCode)="r">
                <span class="mono">{{ r.item.poCode || '—' }}</span>
              </template>
              <template #cell(chassisNumber)="r">
                <span class="mono">{{ r.item.chassisNumber }}</span>
              </template>
              <template #cell(status)="r">
                <b-badge :variant="chassisStatusVariant(r.item.status)">
                  {{ chassisStatusLabel(r.item.status) }}
                </b-badge>
              </template>
              <template #cell(expectedDeliveryDate)="r">
                {{ formatDateOnly(r.item.expectedDeliveryDate) }}
              </template>
            </b-table>
          </b-tab>

          <!-- Tab 3: Lịch sử thanh toán -->
          <b-tab title="Lịch sử thanh toán" @click="loadPayments">
            <b-table
              :items="paymentRows"
              :fields="paymentFields"
              :busy="paymentsLoading"
              responsive
              bordered
              hover
              show-empty
              empty-text="Chưa có khoản thanh toán nào."
            >
              <template #table-busy>
                <div class="text-center text-primary my-3">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Đang tải...</strong>
                </div>
              </template>
              <template #cell(createdAt)="r">
                {{ formatDateOnly(r.item.createdAt) }}
              </template>
              <template #cell(purchaseOrderCode)="r">
                <span v-if="r.item.purchaseOrderCode" class="mono">{{
                  r.item.purchaseOrderCode
                }}</span>
                <span v-else class="text-muted">Trả gộp</span>
              </template>
              <template #cell(amount)="r">
                <b class="po-pay-amount">{{ formatPrice(r.item.amount) }}</b>
              </template>
              <template #cell(paymentMethod)="r">
                {{ r.item.paymentMethod || '—' }}
              </template>
              <template #cell(note)="r">
                {{ r.item.note || '—' }}
              </template>
            </b-table>
          </b-tab>

          <!-- Tab 4: Đáo hạn bảo lãnh/phong tỏa -->
          <b-tab title="Đáo hạn BL/Phong tỏa" @click="loadGuaranteeDue">
            <b-table
              :items="guaranteeRows"
              :fields="guaranteeFields"
              :busy="guaranteeLoading"
              responsive
              bordered
              hover
              show-empty
              empty-text="Không có PO bảo lãnh/phong tỏa nào còn hiệu lực."
            >
              <template #table-busy>
                <div class="text-center text-primary my-3">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Đang tải...</strong>
                </div>
              </template>
              <template #cell(poCode)="r">
                <span class="mono">{{ r.item.poCode || '—' }}</span>
              </template>
              <template #cell(paymentMethod)="r">
                {{ paymentMethodLabel(r.item.paymentMethod) }}
              </template>
              <template #cell(guaranteeDueDate)="r">
                {{ formatDateOnly(r.item.guaranteeDueDate) }}
              </template>
              <template #cell(dueStatus)="r">
                <b-badge :variant="dueBadge(r.item.guaranteeDueDate).variant">
                  <i
                    :class="[
                      'po-over-icon',
                      dueBadge(r.item.guaranteeDueDate).icon,
                    ]"
                  ></i>
                  {{ dueBadge(r.item.guaranteeDueDate).text }}
                </b-badge>
              </template>
            </b-table>
          </b-tab>
        </b-tabs>
      </template>
    </KTCodePreview>

    <!-- Modal: Thanh toán công nợ NPP -->
    <b-modal
      v-model="payDialog.show"
      title="Thanh toán công nợ NPP"
      hide-footer
      no-close-on-backdrop
    >
      <p class="text-muted small mb-2">
        Nhà phân phối: <b>{{ payDialog.providerName }}</b>
      </p>
      <p class="text-muted small mb-3">
        Công nợ thực tế còn lại:
        <b class="text-danger">{{ formatPrice(payDialog.payableDebt) }}</b>
      </p>
      <b-form-group>
        <label>Gắn với PO (tùy chọn)</label>
        <b-form-select
          v-model="payDialog.purchaseOrderId"
          :options="payDialog.poOptions"
          size="sm"
        />
      </b-form-group>
      <b-form-group>
        <label>Số tiền <span class="text-danger">*</span></label>
        <b-form-input
          v-model="payDialog.amount"
          type="text"
          v-mask="mask"
          size="sm"
          class="text-right"
          placeholder="0"
        />
      </b-form-group>
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group>
            <label>Ngày thanh toán</label>
            <b-form-input
              v-model="payDialog.paymentDate"
              type="date"
              size="sm"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group>
            <label>Hình thức</label>
            <b-form-select
              v-model="payDialog.paymentMethod"
              :options="paymentMethodOptions"
              size="sm"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group>
        <label>Ghi chú</label>
        <b-form-textarea v-model="payDialog.note" size="sm" :rows="2" />
      </b-form-group>
      <b-alert v-if="payDialog.error" variant="danger" show class="py-2">
        {{ payDialog.error }}
      </b-alert>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          @click="payDialog.show = false"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="success"
          :disabled="payDialog.submitting"
          @click="submitPayment"
        >
          <b-spinner v-if="payDialog.submitting" small class="mr-1"></b-spinner>
          Xác nhận thanh toán
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';

import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import PurchaseOrderService from '@/core/services/purchaseOrder.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  PO_CHASSIS_STATUS_LABEL,
  PO_PAYMENT_METHOD_LABEL,
} from '@/core/constants/purchaseOrder';
import { checkPermission } from '@/utils/saveDataToLocal';
import {
  makeToastFaile,
  makeToastSuccess,
  convertPrice,
  unMaskMoney,
} from '@/utils/common';

// Mask tiền VND — dấu chấm phân cách nghìn, số nguyên.
const VND_MONEY_MASK = createNumberMask({
  prefix: '',
  thousandsSeparatorSymbol: '.',
  includeThousandsSeparator: true,
  allowDecimal: false,
  allowNegative: false,
});

export default {
  name: 'SupplierDebtReport',
  components: { KTCodePreview },
  data() {
    return {
      activeTab: 0,
      providerOptions: [{ value: null, text: 'Tất cả nhà phân phối' }],
      debtFilter: { providerId: null },
      debtLoading: false,
      debtRows: [],
      debtFields: [
        { key: 'providerName', label: 'Nhà phân phối', class: 'align-middle' },
        {
          key: 'committedDebt',
          label: 'Nợ cam kết',
          class: 'align-middle text-right',
        },
        {
          key: 'payableDebt',
          label: 'Nợ thực tế',
          class: 'align-middle text-right',
        },
        {
          key: 'totalDebt',
          label: 'Tổng nợ',
          class: 'align-middle text-right',
        },
        {
          key: 'creditLimit',
          label: 'Hạn mức',
          class: 'align-middle text-right',
        },
        {
          key: 'overLimit',
          label: 'Tình trạng',
          class: 'align-middle text-center',
        },
        { key: 'actions', label: '', class: 'align-middle text-center' },
      ],
      mask: VND_MONEY_MASK,
      // Hình thức thanh toán — BE lưu chuỗi tự do, FE cho chọn nhanh.
      paymentMethodOptions: [
        { value: null, text: '-- Chọn hình thức --' },
        { value: 'Chuyển khoản', text: 'Chuyển khoản' },
        { value: 'Tiền mặt', text: 'Tiền mặt' },
        { value: 'Quẹt thẻ', text: 'Quẹt thẻ' },
        { value: 'Bù trừ công nợ', text: 'Bù trừ công nợ' },
      ],
      // Modal thanh toán công nợ.
      payDialog: {
        show: false,
        providerId: null,
        providerName: '',
        payableDebt: 0,
        purchaseOrderId: null,
        poOptions: [{ value: null, text: 'Trả gộp (không gắn PO)' }],
        amount: '',
        paymentDate: null,
        paymentMethod: '',
        note: '',
        submitting: false,
        error: '',
      },
      // Tab lịch sử thanh toán.
      paymentsLoading: false,
      paymentRows: [],
      paymentFields: [
        { key: 'createdAt', label: 'Ngày tạo', class: 'align-middle' },
        { key: 'providerName', label: 'Nhà phân phối', class: 'align-middle' },
        { key: 'purchaseOrderCode', label: 'PO', class: 'align-middle' },
        {
          key: 'amount',
          label: 'Số tiền',
          class: 'align-middle text-right',
        },
        { key: 'paymentMethod', label: 'Hình thức', class: 'align-middle' },
        { key: 'note', label: 'Ghi chú', class: 'align-middle' },
      ],
      guaranteeLoading: false,
      guaranteeRows: [],
      guaranteeFields: [
        { key: 'poCode', label: 'Mã PO', class: 'align-middle' },
        { key: 'providerName', label: 'Nhà phân phối', class: 'align-middle' },
        { key: 'paymentMethod', label: 'Hình thức', class: 'align-middle' },
        { key: 'bankName', label: 'Ngân hàng', class: 'align-middle' },
        { key: 'guaranteeNumber', label: 'Số BL', class: 'align-middle' },
        {
          key: 'guaranteeDueDate',
          label: 'Ngày đáo hạn',
          class: 'align-middle text-center',
        },
        {
          key: 'dueStatus',
          label: 'Tình trạng',
          class: 'align-middle text-center',
        },
      ],
      pipelineLoaded: false,
      pipelineLoading: false,
      pipelineRows: [],
      pipelineFields: [
        { key: 'poCode', label: 'Mã PO', class: 'align-middle' },
        {
          key: 'providerName',
          label: 'Nhà phân phối',
          class: 'align-middle',
        },
        { key: 'productName', label: 'Model xe', class: 'align-middle' },
        { key: 'chassisNumber', label: 'Số khung', class: 'align-middle' },
        {
          key: 'status',
          label: 'Trạng thái',
          class: 'align-middle text-center',
        },
        {
          key: 'expectedDeliveryDate',
          label: 'Ngày dự kiến giao',
          class: 'align-middle text-center',
        },
      ],
    };
  },
  computed: {
    canPay() {
      return checkPermission('PURCHASE_ORDER_PAY_DEBT');
    },
  },
  created() {
    this.fetchProviders();
    this.loadDebt();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán' },
      { title: 'Công nợ' },
      { title: 'Nhà phân phối' },
    ]);
  },
  methods: {
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return convertPrice(Number(value)) + ' đ';
    },
    formatDateOnly(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '-';
    },
    chassisStatusLabel(status) {
      return PO_CHASSIS_STATUS_LABEL[status] || status;
    },
    paymentMethodLabel(method) {
      return PO_PAYMENT_METHOD_LABEL[method] || method || '—';
    },
    // Badge đáo hạn: quá hạn (đỏ) / sắp đáo hạn ≤7 ngày (vàng) / còn hạn (xanh).
    dueBadge(date) {
      if (!date)
        return { variant: 'secondary', icon: 'fas fa-minus', text: '—' };
      const days = moment(date)
        .startOf('day')
        .diff(moment().startOf('day'), 'days');
      if (days < 0)
        return {
          variant: 'danger',
          icon: 'fas fa-exclamation-triangle',
          text: `Quá hạn ${Math.abs(days)} ngày`,
        };
      if (days <= 7)
        return {
          variant: 'warning',
          icon: 'fas fa-clock',
          text: days === 0 ? 'Đáo hạn hôm nay' : `Sắp đáo hạn (${days} ngày)`,
        };
      return { variant: 'success', icon: 'fas fa-check', text: 'Còn hạn' };
    },
    async loadGuaranteeDue() {
      this.guaranteeLoading = true;
      try {
        const res = await PurchaseOrderService.getGuaranteeDueReport({
          providerId: this.debtFilter.providerId || undefined,
        });
        const data = get(res, 'data.data') || get(res, 'data') || [];
        this.guaranteeRows = Array.isArray(data) ? data : [];
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được dữ liệu đáo hạn',
        );
        this.guaranteeRows = [];
      } finally {
        this.guaranteeLoading = false;
      }
    },
    chassisStatusVariant(status) {
      return status === 'ON_HOLDING_PENDING_IMPORT' ? 'warning' : 'secondary';
    },
    debtRowClass(item) {
      if (!item) return '';
      return item.overLimit ? 'po-debt-over' : '';
    },
    goBack() {
      this.$router.go(-1);
    },
    async fetchProviders() {
      try {
        const { data } = await ApiService.get('providers/getAll');
        const list = get(data, 'data.data') || get(data, 'data') || [];
        if (Array.isArray(list)) {
          this.providerOptions = [
            { value: null, text: 'Tất cả nhà phân phối' },
            ...list.map((p) => ({
              value: p.id,
              text: p.name || p.shortName || p.id,
            })),
          ];
        }
      } catch (err) {
        console.error('fetchProviders error', err);
      }
    },
    async loadDebt() {
      this.debtLoading = true;
      try {
        const res = await PurchaseOrderService.getSupplierDebtReport({
          providerId: this.debtFilter.providerId || undefined,
        });
        const data = get(res, 'data.data') || get(res, 'data') || [];
        this.debtRows = Array.isArray(data) ? data : [];
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được báo cáo công nợ',
        );
        this.debtRows = [];
      } finally {
        this.debtLoading = false;
      }
    },
    // Mở modal thanh toán cho 1 NPP — nạp danh sách PO (APPROVED/COMPLETED) để gắn.
    async openPayModal(row) {
      this.payDialog = {
        show: true,
        providerId: row.providerId,
        providerName: row.providerName,
        payableDebt: Number(row.payableDebt) || 0,
        purchaseOrderId: null,
        poOptions: [{ value: null, text: 'Trả gộp (không gắn PO)' }],
        amount: '',
        paymentDate: null,
        paymentMethod: null,
        note: '',
        submitting: false,
        error: '',
      };
      this.fetchProviderPOs(row.providerId);
    },
    async fetchProviderPOs(providerId) {
      try {
        const res = await PurchaseOrderService.getAll({
          providerId,
          limit: 200,
        });
        const list = get(res, 'data.data.data') || get(res, 'data.data') || [];
        if (Array.isArray(list)) {
          this.payDialog.poOptions = [
            { value: null, text: 'Trả gộp (không gắn PO)' },
            ...list
              .filter((po) => ['APPROVED', 'COMPLETED'].includes(po.status))
              .map((po) => ({ value: po.id, text: po.code || po.id })),
          ];
        }
      } catch (err) {
        console.error('fetchProviderPOs error', err);
      }
    },
    async submitPayment() {
      this.payDialog.error = '';
      const amount = Number(unMaskMoney(this.payDialog.amount));
      if (!amount || amount <= 0) {
        this.payDialog.error = 'Vui lòng nhập số tiền hợp lệ';
        return;
      }
      if (amount > this.payDialog.payableDebt) {
        this.payDialog.error = `Số tiền vượt công nợ còn lại (${convertPrice(
          this.payDialog.payableDebt,
        )} đ)`;
        return;
      }
      this.payDialog.submitting = true;
      try {
        await PurchaseOrderService.payProviderDebt({
          providerId: this.payDialog.providerId,
          purchaseOrderId: this.payDialog.purchaseOrderId || undefined,
          amount,
          paymentDate: this.payDialog.paymentDate || undefined,
          paymentMethod: this.payDialog.paymentMethod || undefined,
          note: this.payDialog.note || undefined,
        });
        makeToastSuccess('Đã ghi nhận thanh toán công nợ');
        this.payDialog.show = false;
        this.loadDebt();
      } catch (err) {
        this.payDialog.error =
          get(err, 'response.data.message') ||
          err.message ||
          'Thanh toán thất bại';
      } finally {
        this.payDialog.submitting = false;
      }
    },
    async loadPayments() {
      this.paymentsLoading = true;
      try {
        const res = await PurchaseOrderService.getProviderPayments({
          providerId: this.debtFilter.providerId || undefined,
        });
        const data = get(res, 'data.data') || get(res, 'data') || [];
        this.paymentRows = Array.isArray(data) ? data : [];
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được lịch sử thanh toán',
        );
        this.paymentRows = [];
      } finally {
        this.paymentsLoading = false;
      }
    },
    async loadPipeline() {
      // Lazy-load lần đầu mở tab.
      if (this.pipelineLoaded && !this.pipelineLoading) {
        // vẫn cho reload khi click lại — nhẹ.
      }
      this.pipelineLoading = true;
      try {
        const res = await PurchaseOrderService.getPipelineReport({});
        const data = get(res, 'data.data') || get(res, 'data') || [];
        this.pipelineRows = Array.isArray(data) ? data : [];
        this.pipelineLoaded = true;
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được dữ liệu hàng đang về',
        );
        this.pipelineRows = [];
      } finally {
        this.pipelineLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$po-border: #e5eaee;
$po-text-muted: #b5b5c3;

.po-report {
  .po-filter-card {
    background: #fff;
    border: 1px solid $po-border;
    border-radius: 6px;
    padding: 12px 14px;
  }
  .po-label {
    font-size: 11px;
    color: $po-text-muted;
    margin-bottom: 4px;
    display: block;
    font-weight: 500;
  }
  .mono {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }
  .po-over-icon {
    font-size: 9px;
    margin-right: 2px;
    color: inherit !important;
  }
  .po-pay-icon {
    font-size: 11px;
    margin-right: 3px;
    color: inherit !important;
  }
  .po-pay-amount {
    color: #2e7d32;
  }
}
</style>

<style lang="scss">
// Không scope: tô đỏ row overLimit do b-table render <tr>.
.po-debt-table .po-debt-over > td {
  background-color: #ffe2e5 !important;
}
</style>
